/* pricing.js — System A pricing (the live app / quote engine).
 * Ported from lhc-app.html: lhcPlacementUnitPriceEUR (~28877), pvModulePrice/PG_MULT
 * (~38278), buildQuoteLines (~38299), quote totals (~38842), updatePrice (~45732), fmtEUR.
 *
 * NB: the prototype has TWO pricing systems. This module ports SYSTEM A (placements +
 * MODULE_LIBRARY + kitchenWizard) — the one driving the footer total and the quote.
 * System B (the Catalog-Technician per-component build-up: priceCabinet / computeDoorCost /
 * PRICE_GROUPS €/m²) is a separate concern and is NOT ported here.
 *
 * Pure: all inputs passed explicitly (placements, lib, wizard, walls). lib = { [type]: def }.
 */
import { normalizeModel3d, materialSlotsOf } from "./modelResolver";
import { resolveIdx, resolveTok, resolveTok as _resolveTokSp } from "./catalogIds";
import { buildSurfaces, surfaceMaterial, worktopQuantities, splashQuantities } from "./surfaces";
import { resolvePricing, getPricing, pgMultOf } from "./pricingSettings";
import { formatMoney } from "../../lib/money";
import { isKitchenDef } from "./families";
import { resolveFabric, fabricDelta, fabricZonesFor, familyOf } from "./fabrics";

const _round2 = (n) => Math.round(n * 100) / 100;

/** Does a technician worktop material carry QUANTITY rates? (Then the quote
 *  bills the measured run and the flat per-module delta must NOT apply.) */
export function surfaceHasRates(mat) {
  return !!(mat && (mat.pricePerM2 > 0 || mat.edgePricePerM > 0 || mat.jointPrice > 0 || mat.cutoutPrice > 0));
}

export function isCustomSized(p) {
  const cfg = p && p.config;
  return !!(cfg && (cfg.customWidthMm != null || cfg.customHeightMm != null || cfg.customDepthMm != null));
}

/** lhcEffDef: catalog def with per-placement custom width/height applied (metres). (~31685) */
export function effDef(p, lib) {
  const def = lib[p.type];
  if (!def) return null;
  const cfg = p.config;
  const hasW = cfg && cfg.customWidthMm != null;
  const hasH = cfg && cfg.customHeightMm != null;
  const hasD = cfg && cfg.customDepthMm != null;
  if (hasW || hasH || hasD) {
    const eff = { ...def };
    if (hasW) eff.width = cfg.customWidthMm / 1000;
    if (hasH) eff.height = cfg.customHeightMm / 1000;
    if (hasD) eff.depth = cfg.customDepthMm / 1000;
    eff._isCustomSized = true;
    return eff;
  }
  return def;
}

/** Footer per-placement price: G1 base + 20% surcharge when custom-sized. (~28877) */
export function placementUnitPriceEUR(p, lib) {
  const def = lib[p.type];
  const base = (def && def.price) || 0;
  return base + (isCustomSized(p) ? Math.round(base * (getPricing().customSizePct / 100)) : 0);
}

export function priceGroupMult(wizard) {
  const pg = (wizard && wizard.priceGroup) || "G1";
  return pgMultOf(pg);
}

/** Grouped module price at the wizard's price group: priceG{pg} or round(G1 * PG_MULT). (~38287) */
export function pvModulePrice(def, wizard, cfg) {
  if (!def) return 0;
  const pg = (wizard && wizard.priceGroup) || "G1";
  const key = "price" + pg;
  if (def[key] != null) return def[key];
  return Math.round((def.priceG1 || def.price || 0) * pgMultOf(pg, cfg));
}

/** Footer total (kitchens): sum of per-placement G1+surcharge prices. (~45732) */
export function footerTotal(placements, lib) {
  return (placements || []).reduce((s, p) => s + placementUnitPriceEUR(p, lib), 0);
}

/** 2026-07-22 master-definition mandate: per-option price DELTAS from the Cabinet
 *  Technician catalog. Only CATALOG-id selections price (the legacy static mcv3
 *  vocabulary is a 0-delta, so pre-catalog projects keep their exact totals).
 *  Lighting math = cabinet.html computeLightingCost (drawer size nearest to unit
 *  width + transformer; vertical €/m × height × 2 sides; horizontal €/m × width).
 *  Deterministic order; parity-locked with backend config_option_deltas. */
export function configOptionDeltas(blob, def, config, opts) {
  const out = [];
  if (!blob || !config) return out;
  const round2 = (n) => Math.round(n * 100) / 100;
  // STABLE IDS (2026-08-22): "carc-u<uid>" resolves by uid, "carc-K" stays the
  // legacy positional read-alias — same for every family below (JS≡PY).
  const cIdx = resolveIdx(blob.colors, config.carcass, "carc");
  if (cIdx >= 0) {
    const c = (blob.colors || [])[cIdx];
    if (c && c.price > 0) out.push({ key: "carcass", code: "OPT-CARC", label: "Carcass · " + (c.name || String(config.carcass)), val: round2(c.price) });
  }
  // front colour "pgN-cK": groups[g==N].doors[K].price — the technician's
  // per-front-colour delta (hybrid-modular mandate §8; 0 when unpriced).
  const fm = /^pg(\d+)-c((?:u)?\d+)$/.exec(String(config.doorColor || ""));
  if (fm) {
    const g = (blob.groups || []).find((x) => x && parseInt(x.g, 10) === +fm[1]);
    const dIdx = g ? resolveTok(g.doors, fm[2]) : -1;
    const dcol = dIdx >= 0 ? (g.doors || [])[dIdx] : null;
    if (dcol && dcol.price > 0) out.push({ key: "front", code: "OPT-FRONT", label: "Front · " + (dcol.name || ("PG" + fm[1] + " colour")), val: round2(dcol.price) });
  }
  // front layout variant (Phase 2): the technician's per-unit row layouts —
  // def.frontLayouts[].price is the absolute delta for the selected variant.
  const flv = String(config.frontLayout || "");
  if (flv) {
    const fl = ((def && def.frontLayouts) || []).find((x) => x && String(x.id) === flv);
    if (fl && fl.price > 0) out.push({ key: "layout", code: "OPT-LAYOUT-" + fl.id, label: "Front layout · " + (fl.label || fl.id), val: round2(fl.price) });
  }
  const h = (blob.handles || []).find((x) => x && x.code === config.handle);
  if (h && h.price > 0) out.push({ key: "handle", code: "OPT-" + h.code, label: "Handle · " + (h.name || h.code), val: round2(h.price) });
  // gola profile (config.gola = "gola-K", the app-wide index-based id)
  const gI = resolveIdx(blob.gola, config.gola, "gola");
  if (gI >= 0) {
    const g = (blob.gola || [])[gI];
    if (g && g.price > 0) out.push({ key: "gola", code: "OPT-GOLA-" + gI, label: "Gola · " + (g.name || ("Profile " + (gI + 1))), val: round2(g.price) });
    // GOLA COLOUR (2026-08-15): the shared Gola Colours pool — an index-based
    // finish delta, charged only when a PROFILE is selected (same discipline
    // as the handle finishes). Mirrored in pricing_system_a.py (JS≡PY).
    const gcI = resolveIdx(blob.golaColors, config.golaColor, "gc");
    if (gcI >= 0) {
      const gc = (blob.golaColors || [])[gcI];
      if (gc && gc.price > 0) out.push({ key: "golaColor", code: "OPT-GOLACOL-" + gcI, label: "Gola finish · " + (gc.name || ("Finish " + (gcI + 1))), val: round2(gc.price) });
    }
  }
  // backsplash (config.backsplash = technician MatList NAME, like worktops)
  // — same ONE-CHARGE rule: an m²-rated splash is billed from the measured
  // run; only flat-priced materials keep the per-module delta.
  const bsName = String(config.backsplash || "");
  /* opts.splashPanel === false ⇒ the smart rule removed this module's panel
   * (island / back-to-back / pulled off the wall) — a flat charge for a panel
   * that exists nowhere is money without geometry (2026-08-29). Callers with
   * no wall context (module editor, bench) omit opts and keep the charge. */
  if (bsName && bsName !== "None" && !(opts && opts.splashPanel === false)) {
    const bs = (blob.backsplash || []).find((x) => x && x.name === bsName);
    if (bs && bs.price > 0 && !(bs.pricePerM2 > 0)) out.push({ key: "backsplash", code: "OPT-BACKSPLASH", label: "Backsplash · " + bs.name, val: round2(bs.price) });
  }
  // SIDE PANELS (2026-08-29): the technician's end panel, charged PER PANEL
  // (Both = ×2) — flat entry price + optional €/m² on the panel's real face
  // (auto size unless the module overrides it). JS≡PY: pricing_system_a.py
  // config_option_deltas mirrors this block number for number.
  const spId = String(config.sidePanel || "");
  if (spId && spId !== "None") {
    const spTok = /^sp-(.+)$/.exec(spId);
    const spK = spTok ? _resolveTokSp(blob.sidePanels || [], spTok[1]) : -1;
    const sp = spK >= 0 ? (blob.sidePanels || [])[spK] : null;
    if (sp) {
      const _posStr = String(config.sidePanelPos || "L");
      let sides = ["L", "R", "T", "B", "K"].filter((c) => _posStr.indexOf(c) >= 0);
      if (!sides.length) sides = ["L"];
      const nPanels = sides.length;
      // CAD auto board (2026-08-29b, his fit picture): floor → box top,
      // front flush with the door face — the m² charge uses the REAL board.
      // PER-PANEL records (2026-08-30j): a side's own {h,d} wins over the
      // module-wide override, which wins over auto — same chain the 3D uses.
      // PER-SIDE ENTRIES (2026-08-31): each side bills ITS OWN material —
      // grouped into one line per distinct entry, honest labels per material
      const perEntry = new Map();
      sides.forEach((s2) => {
        const sc = (config.sidePanelSideCfg || {})[s2] || {};
        const exM0 = (v) => (Number(v) || 0) / 1000;
        const hM = (sc.h0 > 0 ? sc.h0 / 1000 : sc.h > 0 ? sc.h / 1000
          : config.sidePanelHmm > 0 ? config.sidePanelHmm / 1000 : (def.height || 0))
          + exM0(sc.hU) + exM0(sc.hD);
        const dM = (sc.d > 0 ? sc.d / 1000
          : config.sidePanelDmm > 0 ? config.sidePanelDmm / 1000
            : ((def.depth || 0) - 0.002));   // point 0 (box back) -> door face
        // a CAP's real face is width × depth (2026-08-30k) — auto width =
        // module width + one thickness per fitted L/R side (engine parity)
        const isCap = s2 === "T" || s2 === "B";
        const thkM = Math.max(0.006, (Number(sp.thicknessMm) || 18) / 1000);
        const capW = (def.width || 0)
          + (sides.indexOf("L") >= 0 ? thkM : 0) + (sides.indexOf("R") >= 0 ? thkM : 0);
        // 2026-08-30m: point-0 base override + remembered directional
        // extensions (wL/wR, dB/dF) — the billed face is the REAL total board
        const exM = (v) => (Number(v) || 0) / 1000;
        const wM = (sc.w0 > 0 ? sc.w0 / 1000 : sc.w > 0 ? sc.w / 1000 : capW) + exM(sc.wL) + exM(sc.wR);
        const dM2 = (sc.d0 > 0 ? sc.d0 / 1000 : dM) + exM(sc.dB) + exM(sc.dF);
        // the BACK board's face is span x height (2026-08-31)
        const faceA = s2 === "K" ? wM * hM : isCap ? wM * dM2 : hM * dM2;
        let e2 = sp;
        if (sc.sp) {
          const k2 = _resolveTokSp(blob.sidePanels || [], String(sc.sp));
          if (k2 >= 0 && blob.sidePanels[k2]) e2 = blob.sidePanels[k2];
        }
        const each = (e2.price > 0 ? e2.price : 0) + (e2.pricePerM2 > 0 ? e2.pricePerM2 * faceA : 0);
        const gk = e2.uid != null ? "u" + e2.uid : (e2.name || "?");
        const g = perEntry.get(gk) || { entry: e2, count: 0, total: 0 };
        g.count += 1; g.total += each;
        perEntry.set(gk, g);
      });
      perEntry.forEach((g) => {
        if (!(g.total > 0)) return;
        out.push({
          key: "sidePanel", code: "OPT-SIDEPANEL",
          label: "Side panel · " + (g.entry.name || "Panel") + (g.count === 2 ? " ×2" : g.count > 2 ? " ×" + g.count : ""),
          val: round2(g.total),
        });
      });
      // COLOUR SURCHARGE (2026-08-29d): a priced pool colour bills per panel;
      // "match" and the entry's own finish are free. JS≡PY.
      // PER SIDE (2026-09-01): each side's own colour (sideCfg.col) wins over
      // the module-wide pick; grouped into one line per priced colour
      const perCol = new Map();
      sides.forEach((s2) => {
        const sc = (config.sidePanelSideCfg || {})[s2] || {};
        const colId = sc.col !== undefined ? String(sc.col || "") : String(config.sidePanelColor || "");
        const tk = /^spc-(.+)$/.exec(colId);
        if (!tk) return;
        const k2 = _resolveTokSp(blob.sidePanelColors || [], tk[1]);
        const spc = k2 >= 0 ? (blob.sidePanelColors || [])[k2] : null;
        if (!(spc && spc.price > 0)) return;
        const gk = spc.uid != null ? "u" + spc.uid : (spc.name || "?");
        const g = perCol.get(gk) || { spc, count: 0 };
        g.count += 1; perCol.set(gk, g);
      });
      perCol.forEach((g) => {
        out.push({
          key: "sidePanelColor", code: "OPT-SPCOL",
          label: "Side panel colour · " + (g.spc.name || "Colour") + (g.count === 2 ? " ×2" : g.count > 2 ? " ×" + g.count : ""),
          val: round2(g.spc.price * g.count),
        });
      });
      if (false) {
        const spc = null;
        if (spc && spc.price > 0) out.push({
          key: "sidePanelColor", code: "OPT-SPCOL",
          label: "Side panel colour · " + (spc.name || "Colour") + (nPanels === 2 ? " ×2" : ""),
          val: round2(spc.price * nPanels),
        });
      }
    }
  }
  const dr = (((blob.equipment || {}).drawers) || []).find((x) => x && x.code === config.drawerRunner);
  if (dr && dr.price > 0) out.push({ key: "drawer", code: "OPT-" + dr.code, label: "Drawer runner · " + ((dr.brand ? dr.brand + " " : "") + (dr.model || dr.code)), val: round2(dr.price) });
  // EQUIPMENT (2026-08-18 Edit Module tab): appliance picks — the entry's €
  // from the technician's Equipment Library, CODE-keyed. Mirrored in
  // pricing_system_a.py (JS≡PY).
  [["eqTaps", "taps", "Tap"], ["eqHobs", "hobs", "Hob"], ["eqOvens", "ovens", "Oven"],
   ["eqHoods", "hoods", "Hood"], ["eqFridges", "fridges", "Fridge"], ["eqDishwashers", "dishwashers", "Dishwasher"]]
    .forEach(([key, cat, lbl]) => {
      const code = String(config[key] || "");
      if (!code || code === "None") return;
      const it = (((blob.equipment || {})[cat]) || []).find((x) => x && x.code === code);
      if (!it || !(it.price > 0)) return;
      const nm = (it.brand ? it.brand + " " : "") + (it.model || it.code);
      out.push({ key: "equipment", code: "OPT-EQ-" + it.code, label: lbl + " · " + nm, val: round2(it.price) });
    });
  // interior accessories (Phase 1): additive equipment-library layers —
  // cfg.interior = [{cat, code, qty}] priced item.price × qty per entry.
  if (Array.isArray(config.interior)) {
    config.interior.forEach((e) => {
      if (!e || !e.cat || !e.code) return;
      const it = (((blob.equipment || {})[e.cat]) || []).find((x) => x && x.code === e.code);
      if (!it || !(it.price > 0)) return;
      const qty = Math.max(1, Math.min(resolvePricing(blob.pricing).maxAccessoryQty, parseInt(e.qty, 10) || 1));
      const nm = (it.brand ? it.brand + " " : "") + (it.model || it.code);
      out.push({ key: "interior", code: "OPT-INT-" + it.code, label: "Interior · " + nm + (qty > 1 ? " ×" + qty : ""), val: round2(it.price * qty) });
    });
  }
  // plinth material (pmat KEY or index form "plinth-K") → blob.pmat[].price —
  // the field existed on the technician page; it now actually prices.
  const pcfg = config.plinth;
  if (pcfg && typeof pcfg === "object" && pcfg.enabled !== false && pcfg.material) {
    const pmKey = String(pcfg.material);
    const pI = resolveIdx(blob.pmat, pmKey, "plinth");
    const pm = (blob.pmat || []).find((x, i) => x && ((x.key && String(x.key) === pmKey) || (!x.key && i === pI)));
    if (pm && pm.price > 0) out.push({ key: "plinth", code: "OPT-PLINTH", label: "Plinth · " + (pm.name || pmKey), val: round2(pm.price) });
  }
  // worktop (config.countertop = technician worktop material NAME): only
  // CATALOG names price — the legacy static list stays a 0-delta.
  // 2026-08-18 ONE-CHARGE RULE: a material with QUANTITY rates (€/m², edge,
  // joint, cutout) is billed from the measured run on the quote — the flat
  // per-module delta would DOUBLE-charge it, so it only applies to legacy
  // flat-priced materials.
  const wtName = String(config.countertop || "");
  if (wtName && wtName !== "None") {
    const wt = (blob.worktops || []).find((x) => x && x.name === wtName);
    if (wt && wt.price > 0 && !surfaceHasRates(wt)) out.push({ key: "worktop", code: "OPT-WORKTOP", label: "Worktop · " + wt.name, val: round2(wt.price) });
  }
  // cuts: FREE unless the technician catalog defines a price for the service
  // (blob.pricing.topCut, €/cut) — never hardcoded. Counts every cut on the
  // instance (polygon cuts + a legacy corner notch).
  const cutCount = ((config.cuts && config.cuts.length) || 0) + ((config.topCut && config.topCut.enabled) ? 1 : 0);
  if (cutCount > 0) {
    const fee = (blob.pricing && Number(blob.pricing.topCut)) || 0;
    if (fee > 0) out.push({ key: "topcut", code: "OPT-TOPCUT", label: "Custom cut" + (cutCount > 1 ? " ×" + cutCount : ""), val: round2(fee * cutCount) });
  }
  const lt = config.lighting && config.lighting.type;
  if (lt === "drawer" || lt === "vertical" || lt === "horizontal") {
    const L = blob.lighting || {};
    const tr = L.transformer || 0;
    const wCm = ((def && def.width) || 0) * 100;
    let cost = null, label = null;
    if (lt === "drawer") {
      let best = null;
      (((L.drawer || {}).sizes) || []).forEach((s) => { if (!best || Math.abs(s.cm - wCm) < Math.abs(best.cm - wCm)) best = s; });
      if (best) { cost = (best.price || 0) + tr; label = "Drawer lighting " + best.cm + "cm"; }
    } else if (lt === "vertical") {
      const hM = (def && def.height) || 0;
      if ((L.vertical || {}).pricePerM) { cost = L.vertical.pricePerM * hM * 2 + tr; label = "Vertical lighting (pair)"; }
    } else if ((L.horizontal || {}).pricePerM) {
      cost = L.horizontal.pricePerM * (wCm / 100) + tr; label = "Horizontal lighting";
    }
    if (cost != null && cost > 0) out.push({ key: "lighting", code: "OPT-LIGHT", label, val: round2(cost) });
  }
  // MATERIAL SLOT picks (Stage 3b): priced by GROUP — blob.pricing.materialGroups
  // = { A..F: EUR }, technician-authored on the Pricing page. UNPRICED groups
  // add no line (catalog honesty: never a guessed value). The delta is SIGNED
  // vs the slot's DEFAULT material's group — an upgrade charges, a cheaper
  // pick is an honest discount, equal groups are "Included" (no line).
  // Deterministic role order for JS ≡ PY line-order parity.
  const cfgMats = config.materials;
  if (cfgMats && typeof cfgMats === "object") {
    const slots = materialSlotsOf(normalizeModel3d(def || {}));
    const G = (blob.pricing && blob.pricing.materialGroups) || {};
    const list = blob.materials || [];
    const gv = (m) => (m && m.priceGroup != null && Number(G[m.priceGroup])) || 0;
    const MAT_ROLES = [["front", "Front"], ["carcass", "Carcass"], ["worktop", "Worktop"], ["plinth", "Plinth"], ["handle", "Handle"], ["drawer", "Drawer front"]];
    MAT_ROLES.forEach(([role, lbl]) => {
      const pick = cfgMats[role];
      const slot = slots[role];
      if (!slot || typeof pick !== "string" || !pick || pick === slot.materialId) return;
      const chosen = list.find((x) => x && x.id === pick);
      if (!chosen) return;                          // unknown id: never guess money
      const defMat = list.find((x) => x && x.id === slot.materialId);
      const delta = round2(gv(chosen) - gv(defMat));
      if (delta !== 0) out.push({ key: "material", code: "OPT-MAT-" + role.toUpperCase(), label: "Material · " + lbl + " · " + (chosen.name || pick), val: delta });
    });
  }
  // FABRIC (batch 14): the Fabric Library group's flat € — only on a line that wears fabric
  // (Indoor / Outdoor) and for one of its zones (≡ pricing_system_a.config_option_deltas)
  if (config.fabric) {
    const fr = resolveFabric(blob, config.fabric);
    if (fr && fabricZonesFor(familyOf(def)).indexOf(fr.zone) >= 0) {
      const fd = fabricDelta(blob, config.fabric);
      if (fd) out.push(fd);
    }
  }
  return out;
}

/** moduleQuotePrice(p, lib, blob, wizard) — THE per-unit price the quote charges
 *  for this exact instance: price-group base (pvModulePrice) + custom-size
 *  surcharge + the technician option deltas. The Module Editor displays this
 *  number so editor == cabinet schedule == quotation, always (2026-07-22:
 *  "there must never be different prices for the same configuration"). */
export function moduleQuotePrice(p, lib, blob, wizard) {
  const catDef = (lib && lib[p.type]) || {};
  const base = pvModulePrice(catDef, wizard);
  let total = base + (isCustomSized(p) ? Math.round(base * (getPricing().customSizePct / 100)) : 0);
  if (blob && p && p.config) {
    configOptionDeltas(blob, effDef(p, lib) || catDef, p.config).forEach((d) => { total += d.val; });
  }
  return total;
}

/** buildQuoteLines(): grouped cabinet lines (price-group price) + custom-sized lines
 *  (+20%) + per-cabinet option deltas from the technician catalog (when `blob` is
 *  passed) + per-metre worktop/plinth/cornice lines. (~38299) Option lines use
 *  "auto-opt-" ids, so the ASSEMBLY 12% stays on cabinet base prices only.
 *  Returns {autoLines, manualLines}. */
export function buildQuoteLines(placements, lib, wizard, walls, blob, openings) {
  /* TECHNICIAN-OWNED PRICING (2026-08-18): resolved ONCE for the whole quote —
   * fees, rates and price-group multipliers come from blob.pricing. */
  const pcfg = resolvePricing(blob && blob.pricing);
  const manual = (wizard && wizard.quoteManualLines) || [];
  const auto = [];
  const grouped = {};
  const customSized = [];
  (placements || []).forEach((p) => {
    const catDef = lib[p.type];
    if (!catDef) return;
    if (isCustomSized(p)) customSized.push(p);
    else { if (!grouped[p.type]) grouped[p.type] = { def: catDef, count: 0 }; grouped[p.type].count++; }
  });

  let autoIdx = 0;
  Object.values(grouped)
    .sort((a, b) => pvModulePrice(b.def, wizard) * b.count - pvModulePrice(a.def, wizard) * a.count)
    .forEach((g) => {
      const unitPrice = pvModulePrice(g.def, wizard, pcfg);
      const lineTotal = Math.max(0, unitPrice * g.count);
      // RC polish: dimensions ride the line (ADDITIVE key) so the stored quote
      // snapshot keeps the historical size even if the catalog changes later.
      const dims = g.def.width ? Math.round(g.def.width * 1000) + " × " + Math.round(g.def.height * 1000) + " × " + Math.round(g.def.depth * 1000) + " mm" : undefined;
      auto.push({ id: "auto-cab-" + autoIdx++, code: g.def.code || "", description: g.def.name || g.def.code, qty: g.count, unitPrice, discount: 0, lineTotal, source: "auto", ...(dims ? { dims } : {}) });
    });

  customSized.forEach((p) => {
    const catDef = lib[p.type];
    if (!catDef) return;
    const basePrice = pvModulePrice(catDef, wizard, pcfg);
    const surcharge = Math.round(basePrice * (pcfg.customSizePct / 100));
    const unitPrice = basePrice + surcharge;
    const effD = effDef(p, lib) || catDef;
    const cDims = effD.width ? Math.round(effD.width * 1000) + " × " + Math.round(effD.height * 1000) + " × " + Math.round(effD.depth * 1000) + " mm" : undefined;
    auto.push({ id: "auto-cab-" + autoIdx++, code: (catDef.code || "") + "*", description: catDef.name || catDef.code, qty: 1, unitPrice, discount: 0, lineTotal: unitPrice, source: "auto", _customSized: true, ...(cDims ? { dims: cDims } : {}) });
  });

  /* ONE ORACLE, consulted early (2026-08-29 adversarial review): the surface
   * runs are derived before the option deltas so a flat-priced backsplash on
   * a module whose panel the smart rule suppressed (island / back-to-back /
   * outset) is not billed — "the quote must drop the same splash metres the
   * 3D stops rendering". openings ride along for the same reason the 3D and
   * elevation pass them: the Python quote cuts runs around windows, and the
   * JS preview must bill the same metres (it silently never had them). */
  const _surf = buildSurfaces({
    placements: placements || [], lib, blob: blob || null, wizard: wizard || null,
    effDef: (p, l) => effDef(p, l),
    walls: walls || [],
    openings: openings || [],
    geomOf: (p, def) => ({
      s: (p.absLeft || 0) + (def.width || 0) / 2, q: 0.3, angle: 0,
      w: def.width || 0, topM: def.height || 0, depthM: def.depth || 0,
    }),
  });

  // Cabinet option deltas (Cabinet Technician catalog prices) — grouped by
  // placement type + option so identical picks collapse into one qty-N line.
  if (blob) {
    const agg = {};
    (placements || []).forEach((p) => {
      const def = effDef(p, lib);
      if (!def || !p.config) return;
      configOptionDeltas(blob, def, p.config, { splashPanel: _surf.splashEligible[p.id] === true }).forEach((d) => {
        const k = p.type + "|" + d.code + "|" + d.val;
        if (!agg[k]) agg[k] = { code: d.code, label: d.label + " — " + (def.name || def.code || p.type), val: d.val, count: 0 };
        agg[k].count++;
      });
    });
    let optIdx = 0;
    Object.values(agg).forEach((a) => {
      auto.push({ id: "auto-opt-" + optIdx++, code: a.code, description: a.label, qty: a.count, unitPrice: a.val, discount: 0, lineTotal: Math.round(a.val * a.count * 100) / 100, source: "auto" });
    });
  }

  // rates: the project-level wizard override still wins over the technician's
  const worktopRate = wizard && wizard.worktopRate != null ? wizard.worktopRate : pcfg.worktopRate;
  const plinthRate = wizard && wizard.plinthRate != null ? wizard.plinthRate : pcfg.plinthRate;
  const corniceRate = wizard && wizard.corniceRate != null ? wizard.corniceRate : pcfg.corniceRate;
  const wallName = (wid) => {
    if (wid === "__back") return "Back wall";
    const idx = Array.isArray(walls) ? walls.findIndex((w) => w.id === wid) : -1;
    return idx >= 0 ? "Wall " + (idx + 1) : "Wall";
  };
  const _wtM = {}, _plM = {}, _coM = {};
  (placements || []).forEach((p) => {
    const def = effDef(p, lib);
    if (!def) return;
    if (!isKitchenDef(def)) return;                 // no plinth / cornice metres on furniture lines (JS≡PY)
    const wid = p.wallId || "__back";
    if (def.role === "cabinet-base" || def.role === "appliance") {
      _plM[wid] = (_plM[wid] || 0) + (def.width || 0);
    }
    if (def.role === "cabinet-wall") _coM[wid] = (_coM[wid] || 0) + (def.width || 0);
  });
  /* WORKTOP + BACKSPLASH FROM THE MEASURED RUNS (2026-08-18 user mandate:
   * "pricing must come from what we put in the Cabinet Technician — precise").
   * The oracle derives the SAME runs the 3D and the elevation show (mixed
   * sizes merged, corners butted, elevation trims applied), and the
   * technician's rates price the real quantities. ONE charge per surface:
   *   m²/edge/joint/cutout rates → this quantity line;
   *   legacy flat price          → the per-module OPT-WORKTOP delta (above);
   *   no material picked         → the default per-metre line (engine slab).
   * The old unconditional per-metre WORKTOP charge on every base unit is gone. */
  let _wtLine = 0;
  _surf.worktops.forEach((run) => {
    const mat = surfaceMaterial(blob || null, "worktop", run.material);
    const spec = run.spec;
    if (surfaceHasRates(mat)) {
      const q = worktopQuantities(run);
      const total = _round2(
        q.areaM2 * (spec.pricePerM2 || 0) + q.edgeM * (spec.edgePricePerM || 0) +
        q.joints * (spec.jointPrice || 0) + q.cutouts * (spec.cutoutPrice || 0));
      if (total > 0) auto.push({
        id: "auto-worktop-" + _wtLine++, code: "WORKTOP",
        description: "Worktop · " + run.material + " — " + wallName(run.wallId) + ": "
          + q.lengthM + " m · " + q.areaM2 + " m²"
          + (q.joints ? " · " + q.joints + " joint" + (q.joints > 1 ? "s" : "") : "")
          + (q.cutouts ? " · " + q.cutouts + " cutout" + (q.cutouts > 1 ? "s" : "") : ""),
        qty: 1, unitPrice: total, discount: 0, lineTotal: total, source: "auto",
      });
    } else if (mat && mat.price > 0) {
      /* legacy flat-priced material — billed per module by OPT-WORKTOP */
    } else {
      _wtM[run.wallId] = (_wtM[run.wallId] || 0) + (run.s1 - run.s0);
    }
  });
  let _bsLine = 0;
  _surf.splashes.forEach((run) => {
    const spec = run.spec;
    if (spec.pricePerM2 > 0) {
      const q = splashQuantities(run);
      const total = _round2(q.areaM2 * spec.pricePerM2);
      if (total > 0) auto.push({
        id: "auto-backsplash-" + _bsLine++, code: "BACKSPLASH",
        description: "Backsplash · " + run.material + " — " + wallName(run.wallId) + ": "
          + q.lengthM + " m × " + Math.round(run.heightM * 1000) + " mm · " + q.areaM2 + " m²",
        qty: 1, unitPrice: total, discount: 0, lineTotal: total, source: "auto",
      });
    }
    /* flat-priced splash — billed per module by OPT-BACKSPLASH */
  });
  const pushPerM = (map, rate, code, label) => {
    Object.entries(map).forEach(([wid, wm], i) => {
      if (wm <= 0) return;
      const up = Math.round(rate * 100) / 100;   // 2dp (the 0.1€ snap mangled real rates)
      const lt = Math.round(up * wm * 100) / 100;
      auto.push({ id: "auto-" + code.toLowerCase() + "-" + i, code, description: label + " — " + wallName(wid) + ": " + Math.round(wm * 100) / 100 + " m", qty: 1, unitPrice: lt, discount: 0, lineTotal: lt, source: "auto" });
    });
  };
  pushPerM(_wtM, worktopRate, "WORKTOP", "Worktop");
  pushPerM(_plM, plinthRate, "PLINTH", "Plinth");
  pushPerM(_coM, corniceRate, "CORNICE", "Cornice");

  // Assembly step → quote: Pre-Assembled delivery adds the technician's
  // assembly % of the CABINET lines (blob.pricing.assemblyPct, default 12).
  // The label derives from the SAME number — it can never lie about the rate.
  // Parity-locked with backend build_quote_lines. assemblyPct 0 = fee OFF.
  if (wizard && wizard.packing === "assembled" && pcfg.assemblyPct > 0) {
    const cabSubtotal = auto.reduce((s, l) => s + (String(l.id).indexOf("auto-cab-") === 0 ? (l.lineTotal || 0) : 0), 0);
    if (cabSubtotal > 0) {
      const fee = Math.round(cabSubtotal * (pcfg.assemblyPct / 100) * 100) / 100;
      auto.push({ id: "auto-assembly-0", code: "ASSEMBLY", description: "Pre-assembled delivery (+" + pcfg.assemblyPct + "%)", qty: 1, unitPrice: fee, discount: 0, lineTotal: fee, source: "auto" });
    }
  }

  return { autoLines: auto, manualLines: manual };
}

/** Quote totals: subtotal = lines*(1-discount); total = subtotal.
 *  2026-07-19 user mandate: VAT DOES NOT EXIST on this platform — no tax is
 *  ever computed or added. taxRate/taxAmt stay in the shape (always 0) so
 *  stored quotes and downstream consumers keep their field contract. */
export function quoteTotals(lines, wizard) {
  const overallDisc = (wizard && wizard.quoteDiscount) || 0;
  const linesSubtotal = (lines || []).reduce((s, l) => s + (l.lineTotal || 0), 0);
  const subtotal = linesSubtotal * (1 - overallDisc);
  return { linesSubtotal, subtotal, taxRate: 0, taxAmt: 0, total: subtotal };
}

export function fmtEUR(n) {
  return formatMoney(n);   // ONE money formatter platform-wide (2026-08-18)
}
